import { useMemo, useState } from 'react';
import { Eyebrow, Pill } from '@/components/ui';
import { PROGRAMME_BLOCK, PROGRAMME_DAYS, WEEK_STATE, groupBySuperset } from '@/data/trainerProgramme';
import { RPE_REFERENCE, formatPrescription, formatReps } from '@/lib/prescription';
import { BarbellIcon, CheckIcon, ChevronDownIcon, GridIcon, TimerIcon, TrendUpIcon } from '@/lib/icons';
import { cn } from '@/lib/format';
import { formatWeight } from '@/lib/units';
import { useUnitPreference } from '@/lib/useUnitPreference';

const GROUP_LETTER = ['A', 'B', 'C', 'D', 'E'];

/**
 * The Programme — the coach's spreadsheet, rebuilt as an interface.
 *
 * Everything the sheet encodes is present: movement + variant + modifications,
 * sets × reps (ranges, per side, distance), tempo, RPE target, prescribed load,
 * superset grouping, what was actually logged, and the day's post-session score.
 * Everything a spreadsheet makes painful — the maths, the grouping, the unit
 * conversion, the legend you have to scroll up for — is done for you.
 */
export function Programme() {
  const [dayId, setDayId] = useState(PROGRAMME_DAYS[0]?.id ?? 'day-1');
  const { unit } = useUnitPreference();

  const day = useMemo(() => PROGRAMME_DAYS.find((d) => d.id === dayId) ?? PROGRAMME_DAYS[0], [dayId]);
  const groups = useMemo(() => (day ? groupBySuperset(day) : []), [day]);

  if (!day) return null;

  const totalSets = day.exercises.reduce((sum, exercise) => sum + exercise.line.prescribedSets, 0);

  return (
    <section id="programme" className="border-b border-hairline bg-page py-20">
      <div className="mx-auto max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] px-4 sm:px-6">
        <div className="mb-8 flex flex-col gap-4">
          <Eyebrow>
            <GridIcon className="mr-1 inline text-sm" /> The programme
          </Eyebrow>
          <h2 className="font-display text-h2 font-black uppercase leading-tight tracking-[-0.01em] text-ink md:text-display-2">
            Your coach&rsquo;s plan. None of the spreadsheet.
          </h2>
          <p className="max-w-[76ch] text-body-lg text-ink-muted">
            This is a real block from a real coach, transcribed one-for-one: movement and variant, modifications, sets
            and reps — including ranges, per-side work and distance efforts — tempo, RPE targets, prescribed load,
            superset grouping and the post-session score. Lift2Fit renders it as an interface instead of a grid of cells,
            because a formula you cannot read mid-set is not a training plan.
          </p>
        </div>

        {/* ---------------- block header ---------------- */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-4 rounded-card border border-hairline bg-surface px-5 py-4 shadow-card">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex flex-col">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Block</span>
              <span className="font-display text-[16px] font-black uppercase tracking-[-0.01em] text-ink">
                {PROGRAMME_BLOCK.name}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Lifter</span>
              <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">{PROGRAMME_BLOCK.client}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Started</span>
              <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">
                {new Date(PROGRAMME_BLOCK.startDate).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Cadence</span>
              <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">
                {PROGRAMME_BLOCK.daysPerWeek} days / week
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Pill tone="accent">
              <CheckIcon className="text-xs" /> {day.exercises.length} exercises · {totalSets} working sets
            </Pill>
            <div className="flex flex-wrap items-center justify-end gap-1.5" role="list" aria-label="This week">
              {PROGRAMME_DAYS.map((candidate) => {
                const state = WEEK_STATE.find((entry) => entry.dayIndex === candidate.dayIndex)?.state ?? 'upcoming';
                const isNext = state === 'next';
                return (
                  <span
                    key={`week-${candidate.id}`}
                    role="listitem"
                    title={`${candidate.label} ${candidate.focus}`}
                    className={cn(
                      'flex items-center gap-1.5 border px-2 py-1 text-[10px] font-bold uppercase tracking-[0.06em]',
                      state === 'done'
                        ? 'border-success/40 bg-success/10 text-success'
                        : isNext
                          ? 'border-accent bg-accent/10 text-accent'
                          : 'border-hairline text-ink-muted',
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        state === 'done' ? 'bg-success' : isNext ? 'bg-accent' : 'bg-ink-muted/40',
                      )}
                    />
                    D{candidate.dayIndex} {state === 'done' ? 'done' : isNext ? 'next' : '·'}
                    <span className="sr-only">{state}</span>
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* ---------------- day tabs ---------------- */}
        <div role="tablist" aria-label="Training days" className="mb-5 flex flex-wrap gap-2">
          {PROGRAMME_DAYS.map((candidate) => {
            const active = candidate.id === day.id;
            return (
              <button
                key={candidate.id}
                role="tab"
                aria-selected={active}
                onClick={() => setDayId(candidate.id)}
                className={cn(
                  'flex items-center gap-2 border px-4 py-2.5 text-left transition-colors duration-150',
                  active
                    ? 'border-accent bg-accent text-on-accent'
                    : 'border-hairline bg-surface text-ink hover:border-accent hover:text-accent',
                )}
              >
                <span className="text-[11px] font-bold uppercase tracking-[0.08em]">{candidate.label}</span>
                <span className={cn('text-[10px] font-bold uppercase tracking-[0.08em]', active ? 'opacity-80' : 'text-ink-muted')}>
                  {candidate.focus}
                </span>
              </button>
            );
          })}
        </div>

        {/* ---------------- the day ---------------- */}
        <div className="overflow-hidden rounded-card border border-hairline bg-surface shadow-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline bg-surface-raised px-5 py-3">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-8 w-8 items-center justify-center bg-brand-charcoal text-brand-white">
                <BarbellIcon className="text-base" />
              </span>
              <div className="flex flex-col leading-tight">
                <span className="font-display text-[15px] font-black uppercase tracking-[-0.01em] text-ink">
                  {day.label} · {day.focus}
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
                  {groups.length} groups · {totalSets} sets · target RPE 7
                </span>
              </div>
            </div>
            <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
              <TimerIcon className="text-sm text-accent" /> Rest as prescribed per movement
            </span>
          </div>

          <ol className="divide-y divide-hairline">
            {groups.map((group, groupIndex) => {
              const isSuperset = group.exercises.length > 1;
              return (
                <li key={`${day.id}-${group.group ?? 'solo'}-${groupIndex}`} className="flex flex-col">
                  {isSuperset ? (
                    <div className="flex items-center gap-2 border-b border-hairline bg-page-subtle px-5 py-2">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent text-[10px] font-black text-on-accent">
                        {GROUP_LETTER[groupIndex] ?? groupIndex + 1}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-accent">
                        {group.exercises.length === 3 ? 'Tri-set' : 'Superset'} — performed back to back
                      </span>
                    </div>
                  ) : null}

                  {group.exercises.map((exercise) => (
                    <div
                      key={exercise.id}
                      className="grid grid-cols-1 gap-x-6 gap-y-3 px-5 py-4 lg:grid-cols-[minmax(0,2.1fr)_minmax(0,1.5fr)_minmax(0,1fr)]"
                    >
                      {/* exercise identity */}
                      <div className="flex flex-col gap-1.5">
                        <span className="text-[14px] font-bold leading-tight text-ink">
                          {exercise.movement}
                          {exercise.variant ? (
                            <span className="font-semibold text-ink-muted">, {exercise.variant}</span>
                          ) : null}
                        </span>
                        <span className="flex flex-wrap items-center gap-1.5">
                          <Pill>{exercise.muscleGroup}</Pill>
                          <Pill>{exercise.equipment}</Pill>
                          {exercise.modifications ? <Pill tone="info">{exercise.modifications}</Pill> : null}
                          {exercise.line.perSide ? <Pill tone="accent">per side</Pill> : null}
                          {exercise.line.isBodyweight ? <Pill tone="success">bodyweight</Pill> : null}
                        </span>
                        {exercise.note ? (
                          <span className="mt-0.5 text-[12px] leading-snug text-ink-muted">{exercise.note}</span>
                        ) : null}
                      </div>

                      {/* prescription */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-baseline gap-2">
                          <span className="l2f-tabular font-mono text-[15px] font-bold text-ink">
                            {exercise.line.prescribedSets} × {formatReps(exercise.line)}
                          </span>
                          {exercise.line.tempo ? (
                            <span className="rounded-sm border border-hairline px-1.5 py-[1px] font-mono text-[10px] font-bold text-ink-muted">
                              tempo {exercise.line.tempo}
                            </span>
                          ) : null}
                        </div>
                        <span className="text-[11px] text-ink-muted">{formatPrescription(exercise.line, unit)}</span>
                      </div>

                      {/* week 1, as logged */}
                      <div className="flex flex-col gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                          Week 1 logged
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {exercise.logged.map((set, index) => (
                            <span
                              key={`${exercise.id}-logged-${index}`}
                              className="l2f-tabular rounded-sm border border-success/40 bg-success/10 px-2 py-[2px] font-mono text-[11px] font-bold text-success"
                            >
                              {set.measure === 'distance_m'
                                ? `${set.distanceM} m`
                                : `${set.weightKg > 0 ? formatWeight(set.weightKg, unit) : 'BW'} × ${set.reps}${
                                    exercise.line.perSide ? '/side' : ''
                                  }`}
                            </span>
                          ))}
                        </div>
                        <span className="flex items-center gap-1 text-[11px] font-bold text-accent">
                          <TrendUpIcon className="text-[11px]" />
                          {formatWeight(exercise.line.targetWeightKg, unit)} prescribed
                        </span>
                      </div>
                    </div>
                  ))}
                </li>
              );
            })}
          </ol>

          {/* post-session score strip, mirroring the sheet's footer */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-hairline bg-page-subtle px-5 py-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Post session score</span>
              <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">Mood 4 / 5</span>
              <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">Session RPE 7</span>
              <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">Pain 0 / 10</span>
            </div>
            <span className="text-[11px] text-ink-muted">
              Captured in the app in three taps — no extra sheet, no separate form.
            </span>
          </div>
        </div>

        {/* ---------------- the legend ---------------- */}
        <details className="group mt-5 rounded-card border border-hairline bg-surface">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4">
            <span className="text-[13px] font-bold uppercase tracking-[0.04em] text-ink">
              RPE · reps in reserve · % effort · pain scale
            </span>
            <ChevronDownIcon className="text-base text-ink-muted transition-transform duration-200 group-open:rotate-180" />
          </summary>
          <div className="overflow-x-auto border-t border-hairline">
            <table className="w-full min-w-[620px] border-separate border-spacing-0 text-left">
              <caption className="l2f-visually-hidden">
                The RPE and pain reference scale printed on the coach&rsquo;s programme.
              </caption>
              <thead>
                <tr>
                  {['RPE', 'Reps in reserve', '% effort', 'What it feels like', 'Pain', 'Pain description'].map((heading) => (
                    <th
                      key={heading}
                      scope="col"
                      className="border-b border-hairline px-4 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="l2f-tabular font-mono text-[12px]">
                {RPE_REFERENCE.map((row) => (
                  <tr key={row.label}>
                    <th scope="row" className="border-b border-hairline px-4 py-2 text-left font-bold text-ink">
                      {row.label}
                    </th>
                    <td className="border-b border-hairline px-4 py-2 text-ink-muted">{row.rir}</td>
                    <td className="border-b border-hairline px-4 py-2 text-ink-muted">{row.percentEffort}</td>
                    <td className="border-b border-hairline px-4 py-2 font-sans text-[12px] text-ink">{row.effort}</td>
                    <td className="border-b border-hairline px-4 py-2 font-bold text-ink">{row.painLevel}</td>
                    <td className="border-b border-hairline px-4 py-2 font-sans text-[12px] text-ink-muted">{row.pain}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </div>
    </section>
  );
}
